import { Button, type ButtonProps, styled, useThemeName } from 'tamagui';

import { Text } from '@/components/text';
import { AppleLogo } from '@/components/apple-logo';
import { MICRO_TRANSITION } from '@/theme/motion';
import { borderWidth } from '@/theme/tokens.generated';

// Apple's Human Interface Guidelines specify "Sign in with Apple"
// button appearance directly (black, white, or white-outline, the
// Apple mark + "Sign in with Apple" label, specific corner radius
// options) -- same reasoning as GoogleSignInButton, this doesn't
// borrow this app's own red/mono treatment. Currently only ever
// rendered disabled (Apple sign-in needs a paid Developer Program
// enrollment, not set up yet -- see issue #12) but built to the real
// spec now rather than styled as an afterthought once it's wired up.
//
// Two variants, chosen by theme, per HIG: the enabled black-fill
// button HIG specifies for light backgrounds is a #0d0b08-ish black
// on this app's ~white light page (fine, high contrast) but the SAME
// black-on-black is a #000000 border on a #0d0b08 dark page --
// 1.07:1, invisible (whole-app critique 2026-09-02, only the disabled
// state's lighter border made it visible at all). HIG's own fix is
// the white-outline variant for dark surfaces: transparent fill,
// white border, white mark/label -- swapped in via useThemeName
// rather than this app's own semantic tokens, since Apple's spec is
// deliberately independent of this app's brand palette.
//
// disabledStyle deliberately does NOT use opacity: opacity composites
// the whole subtree, so a dimmed fill washes out or disappears
// depending on theme -- caught by rendering both themes, not by
// reasoning about the style in the abstract. A visible border plus a
// dimmed (not recolored) mark/label keeps "button, unavailable"
// legible in both themes without losing the identity the brand mark
// exists for.
const LightButton = styled(Button, {
  name: 'AppleSignInButtonLight',
  alignSelf: 'center',
  backgroundColor: '#000000',
  borderWidth: borderWidth.hairline,
  borderColor: '#000000',
  borderRadius: '$full',
  paddingHorizontal: '$3',
  paddingVertical: 0,
  height: 48,
  width: '100%',

  // `transition` lives INSIDE hoverStyle/pressStyle, not as a top-level
  // prop -- see primary-button.tsx's doc comment for why (top-level
  // `transition` crashes at runtime with a raw CSS string; the
  // pseudo-style's own `transition` is the real, supported pass-through
  // and is what scopes the web CSS transition to background-color only
  // instead of Tamagui's unset-`transition` default of `all <duration>`).
  hoverStyle: {
    backgroundColor: '#1a1a1a',
    transition: MICRO_TRANSITION,
  },
  pressStyle: {
    backgroundColor: '#333333',
    transition: MICRO_TRANSITION,
  },
  disabledStyle: {
    borderColor: 'rgba(255,255,255,0.32)',
  },
});

const DarkButton = styled(Button, {
  name: 'AppleSignInButtonDark',
  alignSelf: 'center',
  backgroundColor: 'transparent',
  borderWidth: borderWidth.hairline,
  borderColor: '#ffffff',
  borderRadius: '$full',
  paddingHorizontal: '$3',
  paddingVertical: 0,
  height: 48,
  width: '100%',

  hoverStyle: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    transition: MICRO_TRANSITION,
  },
  pressStyle: {
    backgroundColor: 'rgba(255,255,255,0.16)',
    transition: MICRO_TRANSITION,
  },
  disabledStyle: {
    borderColor: 'rgba(255,255,255,0.32)',
  },
});

export function AppleSignInButton({ children, disabled, ...props }: ButtonProps) {
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const labelColor = disabled ? 'rgba(255,255,255,0.6)' : '#ffffff';
  const StyledButton = isDark ? DarkButton : LightButton;

  return (
    <StyledButton {...props} disabled={disabled} icon={<AppleLogo color={labelColor} />}>
      <Text fontFamily="$body" fontWeight="$medium" fontSize="$meta" lineHeight="$meta" color={labelColor}>
        {children}
      </Text>
    </StyledButton>
  );
}
